import { Spinner } from '../ui';
import type { MailboxFolder } from '../../types';
import { FOLDER_SYNC_LABELS } from '../../../../shared/mailbox-sync-labels.js';

/**
 * Состояние синхронизации ящика — по каждой папке, а не одной подписью.
 *
 * **Почему переделано 08.09.** Компонент получал одну дату — максимум
 * `lastSyncAt` по всем папкам — и рисовал «40 мин назад». У живого ящика в
 * это время INBOX синхронизировался, а «Отправленные» сорвались на
 * перезапуске контейнера и не отдали ни одного письма из 1378. По карточке
 * это было неотличимо от полного успеха: сломанная папка не упоминалась
 * вообще.
 *
 * Максимум по папкам — худший вид сводки: он показывает лучшую из папок и
 * прячет остальные. Теперь состояние каждой папки видно отдельно, а сбой —
 * с причиной.
 *
 * Поправку на протухший `syncing` делает сервер: он один знает, когда
 * запись сделана, и правило не должно жить в двух местах.
 */

interface SyncStatusProps {
  /** Ручной запуск в полёте: это не состояние папок, а состояние кнопки. */
  isSyncing?: boolean;
  folders?: MailboxFolder[] | undefined;
}

function formatLastSync(date: string): string {
  const now = new Date();
  const syncDate = new Date(date);
  const diffMs = now.getTime() - syncDate.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'только что';
  if (diffMins < 60) return `${diffMins} мин назад`;
  if (diffHours < 24) return `${diffHours} ч назад`;
  if (diffDays < 7) return `${diffDays} дн назад`;

  return syncDate.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

/** Одна строка на папку: имя, состояние, когда прошла, причина сбоя. */
function FolderRow({ folder }: { folder: MailboxFolder }): JSX.Element {
  const problem = folder.syncStatus === 'error';
  const active = folder.syncStatus === 'syncing';
  // У папки после сброса (смена UIDVALIDITY) состояние `pending`, а дата
  // прошлой удачной синхронизации остаётся в базе. Рядом это читается как
  // «ожидает, но 40 минут назад прошла» — противоречие, которое сбивает
  const датаЗначима = !problem && !active && folder.syncStatus !== 'pending';

  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <span className="text-xs font-medium text-text-primary">{folder.name}</span>
      <span
        className={
          problem
            ? 'text-xs text-accent-danger'
            : active
              ? 'text-xs text-accent-brand'
              : 'text-xs text-text-secondary'
        }
      >
        {/* `??` снимает класс целиком: состояние приходит строкой с
            сервера, и неизвестное значение отрисовалось бы пустотой */}
        {FOLDER_SYNC_LABELS[folder.syncStatus] ?? folder.syncStatus}
      </span>
      {folder.lastSyncAt && датаЗначима && (
        <span className="text-xs text-text-secondary">{formatLastSync(folder.lastSyncAt)}</span>
      )}
      {/* Причина обязательна: без неё «Ошибка» ничего не говорит, а
          оператору решать, звать ли меня */}
      {problem && folder.lastSyncError && (
        <span className="text-xs text-text-secondary basis-full">{folder.lastSyncError}</span>
      )}
    </li>
  );
}

export function SyncStatus({ isSyncing, folders }: SyncStatusProps): JSX.Element {
  if (isSyncing) {
    return (
      <div className="flex items-center gap-2 text-accent-brand">
        <Spinner size="sm" />
        <span className="text-sm">Синхронизация...</span>
      </div>
    );
  }

  if (!folders || folders.length === 0) {
    return <span className="text-sm text-text-secondary">Папки не выбраны</span>;
  }

  // Сводка одной строкой — чтобы состояние читалось без вглядывания в
  // список. Порядок важности: сбой, потом работа, потом неначатые.
  //
  // Ветка «ожидает» обязательна. Без неё папка в `pending` рядом с
  // синхронизированной не попадала ни в одно условие и уезжала в «Все
  // папки синхронизированы» — сводка не молчала, а УТВЕРЖДАЛА успех
  // (найдено ревью 08.09). Случай штатный: `resetSyncState` ставит
  // `pending` при смене UIDVALIDITY, и все папки создаются `pending`.
  //
  // Проверка по состоянию, а не по `lastSyncAt`: у папки, прошедшей
  // сброс, дата прошлой удачной синхронизации остаётся, и по ней папка
  // выглядела бы законченной.
  const broken = folders.filter((f) => f.syncStatus === 'error');
  const running = folders.filter((f) => f.syncStatus === 'syncing');
  const waiting = folders.filter((f) => f.syncStatus === 'pending');
  const никогдаНеСинхронизировался = folders.every((f) => f.lastSyncAt === null);

  return (
    <div className="space-y-1">
      {broken.length > 0 ? (
        <p className="text-sm text-accent-danger">
          {broken.length === 1
            ? `Папка «${broken[0]!.name}» не синхронизирована`
            : `Не синхронизированы папки: ${broken.length}`}
        </p>
      ) : running.length > 0 ? (
        <div className="flex items-center gap-2 text-accent-brand">
          <Spinner size="sm" />
          <span className="text-sm">Синхронизация: {running.map((f) => f.name).join(', ')}</span>
        </div>
      ) : никогдаНеСинхронизировался ? (
        <p className="text-sm text-text-secondary">Не синхронизировался</p>
      ) : waiting.length > 0 ? (
        <p className="text-sm text-text-secondary">
          {waiting.length === 1
            ? `Папка «${waiting[0]!.name}» ждёт синхронизации`
            : `Ждут синхронизации папки: ${waiting.length}`}
        </p>
      ) : (
        <p className="text-sm text-text-secondary">Все папки синхронизированы</p>
      )}

      <ul className="space-y-0.5">
        {folders.map((folder) => (
          <FolderRow key={folder.id ?? folder.name} folder={folder} />
        ))}
      </ul>
    </div>
  );
}
